import React, { useMemo, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { CambioVenta, VentaTablero } from "../../../services/v2/prospects.service";
import AdvisorsPicker from "../../../components/enrollment/advisorsPicker";
import { AdvisorSelection } from "../../../models/EnrollmentHistoryDTO";
import { errorTexto } from "./salesHelpers";

interface Props { campusId: number; ventas: VentaTablero[]; bitacora: CambioVenta[]; puedeEditar: boolean; mesCerrado: boolean; onCambio: () => void; deInforme?: boolean; }

const PLANTEL: { [k: number]: string } = { 1: "MTY", 2: "TRC", 3: "TGU", 4: "SPS", 5: "Online", 7: "SAL" };
const POR_PAGINA = 100;
const ORIGEN: { [k: string]: string } = { tablero: "Tablero", matriculas: "Matrículas", historico: "Histórico", cruce: "Cruce con informe" };
const ACCION: { [k: string]: string } = { asesora: "Cambió quién vendió", no_es_venta: "Marcó «no es venta»", si_es_venta: "Marcó «sí es venta»", carga: "Cargó" };

const fechaCorta = (f: string, soloDia?: boolean) => {
	const [d, h] = f.split(" ");
	const [, m, dd] = d.split("-");
	if (soloDia) {
		return `${dd}/${m}`;
	}
	const [hh, mm] = (h || "00:00").split(":").map(Number);
	return `${dd}/${m} ${((hh + 11) % 12) + 1}:${String(mm).padStart(2, "0")} ${hh < 12 ? "AM" : "PM"}`;
};

/**
 * Ventas del mes, una por renglón (Esteban, 2026-10-30): las supervisoras ven el nombre de cada venta en lugar de llevar su
 * hoja aparte; solo Suamy (Dirección de Ventas, de su gente) y Super Admin cambian quién vendió o marcan «no es venta».
 */
const TableroVentas: React.FC<Props> = ({ campusId, ventas, bitacora, puedeEditar, mesCerrado, onCambio, deInforme }) => {
	const http = HttpManager.getInstance();
	const [equipo, setEquipo] = useState("");
	const [q, setQ] = useState("");
	const [hasta, setHasta] = useState(POR_PAGINA);
	const [abierta, setAbierta] = useState<string | null>(null);
	const [asesoras, setAsesoras] = useState<AdvisorSelection[] | null>(null);
	const [motivo, setMotivo] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const equipos = useMemo(() => Array.from(new Set(ventas.map((v) => v.grupo))).sort(), [ventas]);
	const corregidas = useMemo(() => new Set(bitacora.map((c) => c.venta)), [bitacora]);
	const filtradas = useMemo(() => {
		const t = q.trim().toLowerCase();
		return ventas.filter((v) => (!equipo || v.grupo === equipo)
			&& (!t || (v.cliente + " " + v.curso + " " + v.asesoras.map((a) => a.name).join(" ")).toLowerCase().includes(t)));
	}, [ventas, equipo, q]);

	const abrir = (v: VentaTablero) => {
		setAbierta(v.clave); setAsesoras(v.asesoras.length ? v.asesoras : null); setMotivo(""); setError(null);
	};
	const guardar = (v: VentaTablero, accion: "asesora" | "no_es_venta" | "si_es_venta") => {
		setBusy(true); setError(null);
		http.prospectsService.ventaAjuste(campusId, { tipo: v.tipo, id: v.id, courseId: v.courseId, accion, advisors: accion === "asesora" ? (asesoras || []) : undefined, motivo })
			.then(() => { setAbierta(null); onCambio(); })
			.catch((e) => setError(errorTexto(e)))
			.finally(() => setBusy(false));
	};

	return (
		<div className="tarjeta tb-ventas">
			<div className="tarjeta-cab">
				<h4>{deInforme ? "Matrículas con asesora" : "Ventas del mes"}</h4>
				<span className="apagado">{filtradas.length} de {ventas.length}{deInforme ? " · las matrículas de WAPP a las que se les encontró asesora; el total del mes es el del informe" : ""}
					{mesCerrado ? " · mes cerrado: ya no se cambia" : puedeEditar ? " · puedes corregir las de tu gente" : ""}</span>
			</div>
			<div className="tb-ventas-filtros">
				<select value={equipo} onChange={(e) => { setEquipo(e.target.value); setHasta(POR_PAGINA); }} aria-label="Equipo">
					<option value="">Todos los equipos</option>
					{equipos.map((e) => <option key={e} value={e}>{e}</option>)}
				</select>
				<input type="search" value={q} placeholder="Buscar cliente, curso o asesora" onChange={(e) => { setQ(e.target.value); setHasta(POR_PAGINA); }} />
			</div>
			<div className="waScrollX">
				<table className="tb-tabla">
					<thead><tr><th>Fecha</th><th>Cliente</th><th>Curso</th><th>Asesora</th><th>Equipo</th><th>Plantel</th><th>Cuenta</th>{puedeEditar ? <th /> : null}</tr></thead>
					<tbody>
						{filtradas.slice(0, hasta).map((v) => (
							<React.Fragment key={v.clave}>
								<tr className={v.noEsVenta ? "tb-tachada" : ""}>
									<td style={{ whiteSpace: "nowrap" }} title={v.soloDia ? "Día del depósito" : undefined}>{fechaCorta(v.fecha, v.soloDia)}</td>
									<td>{v.cliente}{corregidas.has(v.tipo + "|" + v.id) ? <span className="tb-corregida" title="Tiene cambios: ver la bitácora">Corregida</span> : null}</td>
									<td>{v.curso}</td>
									<td>{v.asesoras.length ? v.asesoras.map((a) => a.name).join(" + ") : <span className="tb-oro">Sin asesora</span>}</td>
									<td className="apagado">{v.grupo}</td>
									<td className="apagado">{PLANTEL[v.plantel] || v.plantel}</td>
									<td className="tb-cuenta">{v.noEsVenta ? "No es venta" : v.valor === 0.5 ? "½" : "1"}</td>
									{puedeEditar ? <td>{v.editable && abierta !== v.clave ? <button className="enlace" onClick={() => abrir(v)}>Corregir</button> : null}</td> : null}
								</tr>
								{abierta === v.clave ? (
									<tr className="tb-editar"><td colSpan={8}>
										<div className="tb-editar-caja">
											<AdvisorsPicker campusId={v.plantel} value={asesoras} onChange={setAsesoras} disabled={busy} allowNone={false} />
											{v.tipo === "pre" ? <span className="apagado">El cambio aplica a toda la solicitud (todas sus personas).</span> : null}
											<input type="text" value={motivo} maxLength={300} placeholder="Motivo (obligatorio para «No es venta»)" onChange={(e) => setMotivo(e.target.value)} disabled={busy} />
											{error ? <div className="aviso error">{error}</div> : null}
											<div className="tb-editar-botones">
												<button className="btn-wa pri" disabled={busy || !asesoras || !asesoras.length} onClick={() => guardar(v, "asesora")}>Guardar asesora</button>
												{v.noEsVenta
													? <button className="btn-wa" disabled={busy} onClick={() => guardar(v, "si_es_venta")}>Sí es venta</button>
													: <button className="btn-wa" disabled={busy || !motivo.trim()} onClick={() => guardar(v, "no_es_venta")}>No es venta</button>}
												<button className="enlace" disabled={busy} onClick={() => setAbierta(null)}>Cancelar</button>
											</div>
										</div>
									</td></tr>
								) : null}
							</React.Fragment>
						))}
					</tbody>
				</table>
			</div>
			{filtradas.length > hasta ? <button className="enlace" onClick={() => setHasta(hasta + POR_PAGINA)}>Ver {Math.min(POR_PAGINA, filtradas.length - hasta)} más</button> : null}
			{!filtradas.length ? <span className="apagado">Sin ventas con ese filtro.</span> : null}
		</div>
	);
};


/** Bitácora del Tablero: todo cambio manual que movió las ventas del mes, para que no haya dudas de irregularidad. */
export const BitacoraVentas: React.FC<{ ventas: VentaTablero[]; bitacora: CambioVenta[] }> = ({ ventas, bitacora }) => {
	const cliente = useMemo(() => {
		const m: { [k: string]: string } = {};
		ventas.forEach((v) => { const k = v.tipo + "|" + v.id; m[k] = m[k] ? m[k] + " + " + v.curso : v.cliente + " · " + v.curso; });
		return m;
	}, [ventas]);
	return (
		<div className="tarjeta tb-ventas">
			<div className="tarjeta-cab">
				<h4>Bitácora de cambios</h4>
				<span className="apagado">{bitacora.length ? bitacora.length + " cambios manuales" : "Sin cambios manuales este mes"}</span>
			</div>
			{bitacora.length ? (
				<div className="waScrollX">
					<table className="tb-tabla">
						<thead><tr><th>Cuándo</th><th>Quién</th><th>Dónde</th><th>Qué</th><th>Venta</th><th>Antes</th><th>Después</th><th>Motivo</th></tr></thead>
						<tbody>{bitacora.map((c) => (
							<tr key={c.id}>
								<td style={{ whiteSpace: "nowrap" }}>{fechaCorta(c.cuando)}</td><td>{c.quien}</td><td className="apagado">{ORIGEN[c.origen] || c.origen}</td>
								<td>{ACCION[c.accion] || c.accion}</td><td>{cliente[c.venta] || (c.venta.startsWith("hist|") ? "Informe del mes" : "Venta de otro mes o plantel")}</td>
								<td className="apagado">{c.antes || (c.accion === "asesora" ? "Sin asesora" : "")}</td><td>{c.despues || ""}</td><td>{c.motivo || ""}</td>
							</tr>
						))}</tbody>
					</table>
				</div>
			) : null}
			{!bitacora.length ? <span className="apagado">Nadie ha cambiado a mano las ventas de este mes.</span> : null}
		</div>
	);
};

export default TableroVentas;
